import React, { useState } from "react";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import { IoSearchSharp } from "react-icons/io5";
import TableMui from "../../components/mui/TableMui";
import { IconButton, Menu, MenuItem } from "@mui/material";
import Pagination from "../../components/Pagination";
import {
  useDeleteMapMessageOnIdMutation,
  useGetAllMapMessagesQuery,
} from "../../redux/clientApi";
import LoadingScreen from "../LoadingScreen";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import DeletionDialog from "../DeletionDialog";

// CMP CMP CMP
const AdminMapMessages = () => {
  // VARS
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const { data, isLoading } = useGetAllMapMessagesQuery({
    page: currentPage,
    search: search,
  });
  const pagination = data?.data?.pagination || {};
  const messages = data?.data?.messages || [];
  const { t } = useTranslation();
  // Table headers (simple English)
  const headers = {
    name: t("adminMapMessages.name"),
    "routeId.routeName": t("adminMapMessages.route"),
    category: t("adminMapMessages.category"),
    visibility: t("adminMapMessages.visibility"),
    actions: t("adminMapMessages.actions"),
  };
  const [deleteMapMessage, { isLoading: setIsDeletingMapMessage }] =
    useDeleteMapMessageOnIdMutation();
  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [deletionMapMessageId, setDeletionMapMessageId] = useState(null);

  // console.log("messages", messages);

  // FUNCTIONS
  const dialogOpen = () => {
    setAnchorEl(null);

    setTimeout(() => setIsDeletionDialogOpen(true), 100);
  };

  const cancelDelete = () => {
    setIsDeletionDialogOpen(false);
  };

  const confirmDelete = async () => {
    try {
      const res = await deleteMapMessage(deletionMapMessageId);

      if (
        res?.error?.data?.status === "fail" ||
        res?.error?.data?.status === "error"
      ) {
        throw new Error(
          res?.error?.data?.message || "Unable to delete map message",
        );
      }

      const successMessage =
        res?.data?.message || "Map message deleted successfully";

      toast.success(successMessage);

      cancelDelete();
    } catch (error) {
      toast.error(error.message || "Unable to delete map message");
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // JSX JSX JSX

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="mt-[60px] md:mt-[100px] max-w-[1300px] mx-auto pt-4 pb-12 px-6 sm:px-12 bg-white">
      {/* Heading + Search bar */}
      <div className="flex flex-col md:flex-row gap-3 justify-between items-center">
        <div>
          <h2 className="text-[20px] sm:text-[24px] md:text-[30px] lg:text-[40px] font-semibold mt-8 md:mt-0">
            {t("adminMapMessages.mapMessagesManagement")}
          </h2>
          <p className="max-w-[600px] text-[14px] sm:text-[16px]">
            {t("adminMapMessages.manageMessagesPostedByUsers")}
          </p>
        </div>

        <div className="flex items-center w-full sm:w-[calc(30%-30px)] p-1 h-10 bg-[#F7F7F7] border border-[#EECB3D] rounded-md">
          <div className="p-2 bg-[#EECB3D] rounded-md">
            <IoSearchSharp />
          </div>
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            placeholder={t("adminMapMessages.searchByName")}
            className="ml-2 flex-1 p-1 outline-none bg-transparent text-sm"
            // For now static UI → no real search functionality
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="mt-12">
        <TableMui
          th={headers}
          td={messages}
          customFields={[
            {
              name: "category",
              data: (value) => (
                <span className="capitalize">{value || "N/A"}</span>
              ),
            },
            {
              name: "visibility",
              data: (value) => (
                <span className="capitalize font-medium">{value}</span>
              ),
            },
            {
              name: "actions",
              data: (_, row) => (
                <ActionMenu
                  row={row}
                  handleClick={handleClick}
                  anchorEl={anchorEl}
                  handleClose={handleClose}
                  open={open}
                  dialogOpen={dialogOpen}
                  setDeletionMapMessageId={setDeletionMapMessageId}
                />
              ),
            },
          ]}
        />
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages || 0}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {isDeletionDialogOpen && (
        <DeletionDialog
          open={dialogOpen}
          onClose={cancelDelete}
          onConfirm={confirmDelete}
          isDeleting={setIsDeletingMapMessage}
          title="admin-places.dialog.title"
          message="admin-places.dialog.message"
          confirmText="admin-places.dialog.confirm"
          cancelText="admin-places.dialog.cancel"
        />
      )}
    </div>
  );
};

// CMP CMP CMP
const ActionMenu = ({
  row,
  handleClick,
  anchorEl,
  handleClose,
  open,
  dialogOpen,
  setDeletionMapMessageId,
}) => {
  // VARS

  const { t } = useTranslation();

  // FUNCTIONS

  const handleDelete = async (mapMessageId) => {
    setDeletionMapMessageId(mapMessageId);
    dialogOpen();
  };

  // JSX JSX JSX
  return (
    <div>
      <IconButton onClick={handleClick}>
        <PiDotsThreeOutlineVerticalFill />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => handleDelete(row?._id)} sx={{ color: "red" }}>
          {t("adminMapMessages.delete")}
        </MenuItem>
      </Menu>
    </div>
  );
};

export default AdminMapMessages;
